/** The service catalog — Enterprise Phase E, module 4, end-user side.
 *
 * The one screen in this product an end user chooses to visit. Everything else
 * they reach because something broke.
 *
 * Categories here are the real `Service` rows, because that is the only
 * grouping the data has. The ten named categories the brief asks for —
 * Hardware, Software, Microsoft 365, and so on — are a different taxonomy with
 * an icon and a description per category, and nothing in the model carries
 * either. Guessing a mapping from "End User Computing" to "Hardware" would file
 * items under categories nobody chose, so the real services are shown and the
 * target taxonomy is specified in the admin view and docs/UI-BACKLOG.md.
 */
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, LayoutGrid, PackageSearch, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/empty-state'
import { LiveRegion } from '../../components/ui/live-region'
import { Input, Skeleton } from '../../components/ui/primitives'
import { groupByService, searchCatalog, type CatalogEntry } from '../../lib/catalog'
import { cn } from '../../lib/utils'
import { api } from '../../lib/api'

export function Catalog() {
  const [term, setTerm] = useState('')
  const [category, setCategory] = useState<string | null>(null)

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.get<CatalogEntry[]>('/catalog/'),
  })

  const categories = useMemo(() => groupByService(items), [items])
  const visible = useMemo(() => {
    const matched = searchCatalog(items, term)
    return category ? matched.filter((item) => item.service === category) : matched
  }, [items, term, category])
  const groups = useMemo(() => groupByService(visible), [visible])

  if (isLoading) {
    return (
      <>
        <PageHeader title="Service catalog" description="Everything you can ask IT for." />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-32" />
          ))}
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Service catalog"
        description="Everything you can ask IT for. Pick what you need and we will take it from there."
      />

      {/* --- Search and category rail ------------------------------------ */}
      <div className="mb-4 space-y-3">
        <div className="relative max-w-lg">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Search for a laptop, access, software…"
            aria-label="Search the service catalog"
            className="pl-8"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <CategoryChip
            label="All"
            count={items.length}
            active={category === null}
            onClick={() => setCategory(null)}
          />
          {categories.map((entry) => (
            <CategoryChip
              key={entry.key}
              label={entry.label}
              count={entry.items.length}
              active={category === entry.key}
              onClick={() => setCategory(category === entry.key ? null : entry.key)}
            />
          ))}
        </div>
      </div>

      <LiveRegion
        message={`${visible.length} service${visible.length === 1 ? '' : 's'} match your search.`}
      />

      {/* --- Cards -------------------------------------------------------- */}
      {visible.length === 0 ? (
        <EmptyState
          icon={PackageSearch}
          title="Nothing matches that"
          description={
            items.length === 0
              ? 'No catalog items are available to you. Your organisation may not have any published yet.'
              : 'Try a different word, or clear the category filter.'
          }
        />
      ) : (
        groups.map((group) => (
          <section key={group.key} aria-labelledby={`cat-${slug(group.key)}`} className="mb-6">
            <h2
              id={`cat-${slug(group.key)}`}
              className="mb-2.5 flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground"
            >
              <LayoutGrid className="size-3.5 text-muted-foreground" aria-hidden />
              {group.label}
              <Badge variant="neutral" size="sm">
                {group.items.length}
              </Badge>
            </h2>

            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {group.items.map((item) => (
                <li key={item.id}>
                  <CatalogCard item={item} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </>
  )
}

function CategoryChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {label}
      <span className={cn('numeric', active ? 'opacity-80' : 'opacity-60')}>{count}</span>
    </button>
  )
}

function CatalogCard({ item }: { item: CatalogEntry }) {
  const fields = item.form_schema?.fields ?? []

  return (
    <Card className="group h-full transition-colors hover:border-primary/40 hover:bg-accent/30">
      <CardContent className="flex h-full flex-col p-4">
        {/* An icon per item is a specified field that does not exist yet, so
            every card carries the same neutral mark rather than a guess. */}
        <div className="flex items-start gap-2.5">
          <span className="grid size-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
            <LayoutGrid className="size-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-medium text-foreground">{item.name}</h3>
            <p className="truncate text-xs text-muted-foreground">{item.service}</p>
          </div>
        </div>

        <p className="mt-2.5 flex-1 text-xs leading-relaxed text-muted-foreground">
          {fields.length > 0
            ? `Asks for ${fields.length} detail${fields.length === 1 ? '' : 's'}: ${fields
                .slice(0, 3)
                .map((field) => (field.label ?? field.name).toLowerCase())
                .join(', ')}${fields.length > 3 ? '…' : '.'}`
            : 'No extra details needed — just tell us what you want.'}
        </p>

        <Link
          to={`/catalog/${item.id}`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          View and request
          <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" aria-hidden />
        </Link>
      </CardContent>
    </Card>
  )
}

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-')
}
